import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../types/product';
import { useCart } from '../contexts/CartContext';
import { CartItem } from '../types/cart';
import ImageWithFallback from './ImageWithFallback';
import { getLocalizedValue, getCurrentLanguage } from '../config/languageConfig';
import { 
  formatPrice as formatPriceWithCurrency, 
  SupportedCurrency, 
  getCurrencyFromLanguage,
  getPriceFieldByCurrency
} from '../utils/currencyUtils';
import { useOperationTimeCheck } from '../hooks/useOperationTime';
import { useAddressAffordance } from '../contexts/AddressAffordanceContext';
import ServiceClosedModal from './ServiceClosedModal';

interface ShopNames {
  name: string;
  nameEn: string | null;
  nameKo: string | null;
  nameJa: string | null;
  nameZhCn: string | null;
  nameZhTw: string | null;
}

interface ProductItemProps {
  product: Product;
  shopId: string;
  shopName: string;
  shopMultiNames?: ShopNames;
  currency?: SupportedCurrency;
}

const ProductItem: React.FC<ProductItemProps> = ({ 
  product, 
  shopId, 
  shopName,
  shopMultiNames,
  currency
}) => {
  const { cart, addToCart, updateItemQuantity, canceledItemId } = useCart();
  const [showServiceClosedModal, setShowServiceClosedModal] = useState(false);
  const { checkAndExecute } = useOperationTimeCheck();
  const { checkAddressAndExecute } = useAddressAffordance();
  const navigate = useNavigate();

  // 현재 언어 설정에 따라 이름 처리
  const currentLang = getCurrentLanguage();

  // 현재 언어에 기반한 통화 설정 (props로 전달된 currency가 없는 경우)
  const effectiveCurrency = currency || getCurrencyFromLanguage(currentLang);

  // 현재 언어에 맞는 상품명 가져오기
  const displayName = getLocalizedValue(product.productNameKo, {
    en: product.productNameEn,
    ja: product.productNameJa,
    'zh-CN': product.productNameZhCn,
    'zh-TW': product.productNameZhTw
  });

  // 상품에서 현재 언어에 맞는 가격 필드 선택
  const getItemPrice = (): number => {
    const currency = getCurrencyFromLanguage(currentLang);
    const priceField = getPriceFieldByCurrency(currency);
    
    switch (priceField) {
      case 'priceKrw':
        return product.priceKrw;
      case 'priceUsd':
        return product.priceUsd;
      case 'priceJpy':
        return product.priceJpy;
      case 'priceCny':
        return product.priceCny;
      case 'priceTwd':
        return product.priceTwd;
      default:
        return product.price;
    }
  };
  
  // 현재 언어에 맞는 가격 계산
  const currentPrice = getItemPrice();

  // 가격 표시 - 선택된 통화 사용
  const displayPrice = formatPriceWithCurrency(currentPrice, effectiveCurrency);

  // 장바구니에서 현재 아이템의 수량 찾기
  const getCartQuantity = useCallback((): number => {
    if (!cart) return 0;
    
    // 현재 표시중인 기프트샵과 카트에 저장된 레스토랑이 다르면 0 반환
    if (cart.restaurantId !== shopId) return 0;
    
    const cartItem = cart.items.find(item => item.id === product.id);
    return cartItem ? cartItem.quantity : 0;
  }, [cart, product.id, shopId]);
  
  // 현재 수량 상태 관리
  const [quantity, setQuantity] = useState<number>(getCartQuantity());

  // 카트 상태가 변경될 때마다 수량 동기화
  useEffect(() => {
    setQuantity(getCartQuantity());
  }, [cart, getCartQuantity]);

  // 취소된 아이템 감지하여 수량 초기화
  useEffect(() => {
    if (canceledItemId && canceledItemId === product.id) {
      setQuantity(getCartQuantity());
    }
  }, [canceledItemId, product.id, getCartQuantity]);

  // 수량 증가 (주소 체크 + 운영시간 체크)
  const increaseQuantity = () => {
    checkAddressAndExecute(() => {
      checkAndExecute(
        () => {
          // 운영 중일 때 - 기존 로직 실행
          let startQuantity = quantity;
          if (cart && cart.restaurantId !== shopId && quantity === 0) {
            startQuantity = 0;
          }
          
          const newQuantity = startQuantity + 1;
          setQuantity(newQuantity);
          updateCartItem(newQuantity);
        },
        () => setShowServiceClosedModal(true) // 운영시간 외일 때
      );
    }, 'cart');
  };

  // 수량 감소 (운영시간 체크)
  const decreaseQuantity = () => {
    if (quantity > 0) {
      checkAndExecute(
        () => {
          // 운영 중일 때 - 기존 로직 실행
          const newQuantity = quantity - 1;
          setQuantity(newQuantity);
          updateCartItem(newQuantity);
        },
        () => setShowServiceClosedModal(true) // 운영시간 외일 때
      );
    }
  };

  // 장바구니 업데이트
  const updateCartItem = (newQuantity: number) => {
    // 수량이 0이면 아이템 제거 (updateItemQuantity에서 자동으로 처리됨)
    if (newQuantity === 0) {
      updateItemQuantity(product.id, 0);
    } 
    // 처음 추가하는 경우 (혹은 다른 레스토랑 카트가 있을 경우)
    else if (quantity === 0 && newQuantity > 0) {
      // 장바구니 아이템 생성
      const cartItem: CartItem = {
        id: product.id,
        name: displayName,
        price: currentPrice, // 현재 언어에 맞는 가격을 기본 price로 사용
        quantity: newQuantity,
        options: [], // 상품에는 옵션이 없음
        image: product.imageUrl,
        // 다국어 상품 이름 추가
        nameEn: product.productNameEn || undefined,
        // 모든 통화별 가격 정보 저장
        priceKrw: product.priceKrw,
        priceUsd: product.priceUsd,
        priceJpy: product.priceJpy,
        priceCny: product.priceCny,
        priceTwd: product.priceTwd
      };
      
      // 가게 다국어 이름 정보를 저장할 객체
      const shopInfo = {
        // shopName은 현재 언어에 맞는 이름
        restaurantName: shopName,
        // 다국어 이름 정보가 있으면 모두 추가 (null, undefined 검사 추가)
        restaurantNameEn: shopMultiNames?.nameEn || shopName,
        restaurantNameJa: shopMultiNames?.nameJa || shopMultiNames?.nameEn || shopName,
        restaurantNameZhCn: shopMultiNames?.nameZhCn || shopMultiNames?.nameEn || shopName,
        restaurantNameZhTw: shopMultiNames?.nameZhTw || shopMultiNames?.nameEn || shopName,
        restaurantNameKo: shopMultiNames?.nameKo || shopMultiNames?.nameEn || shopName
      };
      
      // CartContext의 addToCart 호출 - 가게 정보 객체도 함께 전달
      addToCart(shopId, shopInfo, cartItem);
    } 
    // 기존 아이템 수량 업데이트 (같은 가게일 경우에만)
    else if (cart && cart.restaurantId === shopId) {
      updateItemQuantity(product.id, newQuantity);
    }
  };

  // 상품 상세 페이지로 이동
  const handleViewProductDetail = () => {
    navigate(`/shop/${shopId}/product/${product.id}`);
  };

  return (
    <>
      <div className="flex flex-col py-2 hover:bg-gray-50 transition-colors">
        <div className="flex items-stretch relative">
          {/* 상품 이미지 */}
          <div 
            className="w-20 h-20 bg-gray-100 rounded-md overflow-hidden flex-shrink-0 mr-3 my-auto cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleViewProductDetail}
          >
            <ImageWithFallback
              src={product.imageUrl || ''}
              alt={displayName}
              className="w-full h-full object-cover"
              fallback="https://source.unsplash.com/random/300x300/?gift"
            />
          </div>
          
          {/* 상품 정보 */}
          <div className="flex-grow flex flex-col justify-between py-1 pr-10">
            <div 
              className="cursor-pointer"
              onClick={handleViewProductDetail}
            >
              <h3 className="text-base font-semibold mb-1 text-left line-clamp-1 hover:text-red-600 transition-colors">{displayName}</h3>
              {/* <p className="text-gray-500 text-xs mb-2 line-clamp-2 text-left">{product.section}</p> */}
            </div>
            
            {/* 가격, 수량 조절 */}
            <div className="mt-auto">
              <div className="flex items-center justify-between">
                {/* 가격 */}
                <div className="font-semibold text-base flex flex-col">
                  {displayPrice}
                  <span className="text-xs text-gray-500">(per 1 piece)</span>
                </div>
                
                {/* 수량 조절 */}
                <div className="flex items-center">
                  <button
                    className={`w-7 h-7 flex items-center justify-center border border-gray-300 rounded-md bg-white ${quantity > 0 ? 'text-gray-700' : 'text-gray-400'}`}
                    onClick={decreaseQuantity}
                    disabled={quantity === 0}
                    aria-label="Decrease quantity"
                  >
                    <span className="text-lg font-medium">−</span>
                  </button>
                  <span className="mx-2 text-base font-medium min-w-[20px] text-center">{quantity}</span>
                  <button
                    className={`w-7 h-7 flex items-center justify-center border rounded-md ${
                      quantity > 0 
                        ? 'bg-red-500 text-white border-red-500' 
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                    onClick={increaseQuantity}
                    aria-label="Increase quantity"
                  >
                    <span className="text-lg font-medium">+</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* 상품 상세보기 버튼 - Material Design 스타일 */}
          <div 
            className="absolute right-0 inset-y-0 flex items-center justify-center w-10 transition-colors hover:bg-gray-100 active:bg-gray-200 rounded-r cursor-pointer"
            role="button"
            aria-label="View product details"
            onClick={handleViewProductDetail}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-gray-400">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
            </svg>
          </div>
        </div>
      </div>

      {/* 운영시간 외 모달 */}
      <ServiceClosedModal 
        isOpen={showServiceClosedModal}
        onClose={() => setShowServiceClosedModal(false)}
      />
    </>
  );
};

export default ProductItem; 